import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { getMediaUrl } from '../utils/mediaUrl';
import { trackContact } from '../utils/metaPixel';

const Services = () => {
  const { getContent } = useContent();
  const location = useLocation();
  const pageHeader = getContent('services.pageHeader');
  const overview = getContent('services.overview');
  const mainServices = getContent('services.mainServices');
  const cta = getContent('services.cta');

  const renderServiceButton = (service, buttonKey, className = 'btn btn-primary') => {
    const btn = service.actions?.[buttonKey];
    if (!btn?.text) return null;

    if (btn.whatsappMessage) {
      return (
        <a
          href={buildWhatsAppUrl(btn.whatsappMessage, null, getContent)}
          className={className}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackContact({ source: 'services_page', label: service.title })}
        >
          <i className={btn.icon || 'fab fa-whatsapp'}></i>
          {btn.text}
        </a>
      );
    }

    if (btn.link) {
      if (btn.link.startsWith('http')) {
        return (
          <a href={btn.link} className={className} target="_blank" rel="noopener noreferrer">
            <i className={btn.icon || 'fas fa-arrow-right'}></i>
            {btn.text}
          </a>
        );
      }
      return (
        <Link to={btn.link} className={className}>
          <i className={btn.icon || 'fas fa-arrow-right'}></i>
          {btn.text}
        </Link>
      );
    }

    if (service.id === 'formation') {
      return (
        <Link to="/formations#formations-grid-section" className={className}>
          <i className={btn.icon || 'fas fa-graduation-cap'}></i>
          {btn.text}
        </Link>
      );
    }

    if (service.id === 'coaching') {
      return (
        <Link to="/experts" className={className}>
          <i className={btn.icon || 'fas fa-user-md'}></i>
          {btn.text}
        </Link>
      );
    }

    return (
      <a
        href={buildWhatsAppUrl(`Bonjour, je suis intéressé(e) par vos services de ${service.title.toLowerCase()}`, null, getContent)}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackContact({ source: 'services_page', label: service.title })}
      >
        <i className={btn.icon || 'fab fa-whatsapp'}></i>
        {btn.text}
      </a>
    );
  };

  // Reorder services: Formation, Séminaires, Coaching, Consulting
  const reorderedServices = React.useMemo(() => {
    if (!mainServices?.services) return [];
    const services = [...mainServices.services];
    const order = ['formation', 'seminaires', 'coaching', 'consulting'];
    return services.sort((a, b) => {
      const indexA = order.indexOf(a.id);
      const indexB = order.indexOf(b.id);
      return indexA - indexB;
    });
  }, [mainServices]);

  // Handle scrolling to hash on page load or hash change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  return (
    <div className="services-page">
      {/* Page Header */}
      <section className="page-header" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Hero Image Layer */}
        {pageHeader?.heroImage && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            zIndex: 0
          }}>
            <img
              src={getMediaUrl(pageHeader.heroImage)}
              alt="Hero"
              style={pageHeader.heroImageSettings ? {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(calc(-50% + ${pageHeader.heroImageSettings.position.x}px), calc(-50% + ${pageHeader.heroImageSettings.position.y}px)) scale(${pageHeader.heroImageSettings.scale})`,
                transformOrigin: 'center center',
                width: 'auto',
                height: 'auto',
                minWidth: '100%',
                minHeight: '100%',
                maxWidth: 'none',
                objectFit: 'cover'
              } : {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        )}
        
        {/* Gradient Overlay - Only if enabled */}
        {pageHeader?.overlayEnabled !== false && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: pageHeader?.overlayColor || '#58c4dc',
            opacity: pageHeader?.overlayOpacity ?? 0.85,
            zIndex: 1
          }}></div>
        )}
        
        {/* Content */}
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="page-header-content">
            <h1>{pageHeader?.title || 'Nos Services'}</h1>
            <p>{pageHeader?.subtitle || ''}</p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      {(overview?.title || overview?.subtitle || (overview?.stats?.length > 0)) && (
        <section className="services-overview">
          <div className="container">
            <div className="overview-content">
              <div className="overview-text">
                {overview.title && <h2>{overview.title}</h2>}
                {overview.subtitle && <p>{overview.subtitle}</p>}
              </div>
              {(overview.stats || []).length > 0 && (
                <div className="overview-stats">
                  {overview.stats.map((stat, index) => (
                    <div key={index} className="service-stat">
                      <div className="stat-icon">
                        <i className={stat.icon || 'fas fa-star'}></i>
                      </div>
                      <h3>{stat.number}</h3>
                      <p>{stat.label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Services Sections with Image + Text Block Layout */}
      {reorderedServices.map((service, index) => (
        <section key={service.id} className={`service-block service-block-${service.color}`} id={service.id}>
          <div className="container">
            <div className={`service-image-text ${index % 2 === 0 ? '' : 'reverse'}`}>
              {/* Image Side */}
              <div className="service-image">
                {service.image ? (
                  <img src={getMediaUrl(service.image)} alt={service.title} />
                ) : (
                  <div className="service-image-placeholder">
                    <i className={service.icon || 'fas fa-star'}></i>
                  </div>
                )}
              </div>

              {/* Text Side */}
              <div className="service-text">
                <h2>{service.title}</h2>
                <p className="service-description">{service.description}</p>
                
                <div className="service-features">
                  <h3>{mainServices?.featuresTitle || 'Ce que vous obtenez :'}</h3>
                  <ul className="features-list">
                    {(service.features || []).map((feature, featureIndex) => (
                      <li key={featureIndex}>
                        <i className="fas fa-check-circle"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="service-actions">
                  {renderServiceButton(service, 'primaryButton')}
                  {renderServiceButton(service, 'secondaryButton', 'btn btn-outline')}
                  {!service.actions?.primaryButton?.text && !service.actions?.secondaryButton?.text && (
                    <a
                      href={buildWhatsAppUrl(`Bonjour, je suis intéressé(e) par vos services de ${service.title.toLowerCase()}`, null, getContent)}
                      className="btn btn-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackContact({ source: 'services_page', label: service.title })}
                    >
                      <i className="fab fa-whatsapp"></i>
                      Contactez-nous
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Call to Action */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>{cta?.title || 'Prêt à Commencer Votre Développement ?'}</h2>
            <p>{cta?.subtitle || 'Choisissez le service qui correspond le mieux à vos besoins et objectifs professionnels'}</p>
            <div className="cta-buttons">
              {(cta?.buttons || []).map((button, index) => (
                button.link?.startsWith('http') ? (
                  <a key={index}
                     href={button.link} 
                     className={`btn btn-${button.type}`}
                     target="_blank" 
                     rel="noopener noreferrer">
                    <i className={button.icon}></i>
                    {button.text}
                  </a>
                ) : (
                  <Link key={index} to={button.link} className={`btn btn-${button.type}`}>
                    <i className={button.icon}></i>
                    {button.text}
              </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;

